/**
 * Billing API Client
 * API calls for manager billing and subscription management
 */

import { z } from 'zod';
import { asIsoDateTimeString, formatTimestamp } from '@resida/shared';
import { apiFetch } from '../lib/api';
import { ApiClientError, triggerReauthRequired } from '../lib/api.core';
import {
  BundleTemplateSchema,
  SubscriptionStatusResponseSchema,
  BillingAddressSchema,
  InvoiceSchema,
  SubscribeResponseSchema,
  AgentAddonQuoteResponseSchema,
  AgentAddonEnableRequestSchema,
  AgentAddonEnableResponseSchema,
  AgentAddonStatusResponseSchema,
  PaymentMethodSetupIntentResponseSchema,
  PaymentMethodConfirmResponseSchema,
  BillingPaymentErrorResponseSchema,
  type DefaultPaymentMethod,
  type InvoicesListResponse,
} from '@resida/api-types/billing';
import { PlanCopyResponseSchema, type PlanCopy } from '@resida/api-types/plan-copy';

// =============================================================================
// TYPES
// =============================================================================

// Derived from BundleTemplateSchema in @resida/api-types/billing
export type BundleTemplate = z.infer<typeof BundleTemplateSchema>;

// Derived from SubscriptionStatusResponseSchema in @resida/api-types/billing
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusResponseSchema>;

// Derived from BillingAddressSchema in @resida/api-types/billing
export type BillingAddress = z.infer<typeof BillingAddressSchema>;

// Derived from SubscribeResponseSchema in @resida/api-types/billing; `savedCard` is present only
// when the saved card was used (BR-112).
export type SubscribeResult = z.infer<typeof SubscribeResponseSchema>;

/**
 * Phase 2 confirm result — subscription activating asynchronously
 */
export interface ConfirmSubscriptionResult {
  subscriptionId: string;
  activating: boolean;
}

// Derived from InvoiceSchema in @resida/api-types/billing
export type Invoice = z.infer<typeof InvoiceSchema>;

type AgentAddonQuote = z.infer<typeof AgentAddonQuoteResponseSchema>;
type AgentAddonEnableParams = z.infer<typeof AgentAddonEnableRequestSchema>;
type AgentAddonEnableResult = z.infer<typeof AgentAddonEnableResponseSchema>;
type AgentAddonStatusResult = z.infer<typeof AgentAddonStatusResponseSchema>;
type PaymentMethodSetupIntentResult = z.infer<typeof PaymentMethodSetupIntentResponseSchema>;
type PaymentMethodConfirmResult = z.infer<typeof PaymentMethodConfirmResponseSchema>;

// =============================================================================
// DISPLAY HELPERS (shared by the payment dialogs)
// =============================================================================

/**
 * Whole-dollar USD from integer cents: "$29", "$1,234.50", "$0.50". Whole amounts drop the
 * ".00" unless `alwaysDecimals` (invoice / plan-price tables, which align on two decimals).
 */
export function formatUsd(cents: number, { alwaysDecimals = false }: { alwaysDecimals?: boolean } = {}): string {
  const dollars = cents / 100;
  const fractionDigits = alwaysDecimals || !Number.isInteger(dollars) ? 2 : 0;
  return `$${dollars.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * "Oct 18" (or "Oct 18, 2026" with `withYear`) for an instant, in the given IANA timezone — a
 * listing period renders in the property's timezone, a billing period (an account has no property
 * timezone) in 'UTC', which is the default so a period boundary never shifts a day with the
 * viewer's locale. '—' for a missing instant.
 */
export function formatPeriodDate(iso: string | null | undefined, timezone = 'UTC', withYear = false): string {
  if (!iso) return '—';
  return formatTimestamp(asIsoDateTimeString(iso), timezone, {
    month: 'short',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' as const } : {}),
  });
}

/** "Visa •••• 4242" — brand + last4 only. */
export function formatCardLabel(card: DefaultPaymentMethod): string {
  const brand = card.brand ? card.brand.charAt(0).toUpperCase() + card.brand.slice(1) : 'Card';
  return `${brand} •••• ${card.last4}`;
}

/**
 * Stripe decline codes that mean the issuer wants the cardholder to authenticate (SCA / 3DS). An
 * off-session charge cannot do that, so it ends as a decline; the landlord is told what that means
 * (and the dialogs offer "use a different card") rather than shown Stripe's internal code.
 */
const AUTHENTICATION_DECLINE_CODES = new Set(['authentication_required', 'invoice_payment_intent_requires_action']);

/** A Stripe decline code as a short reason: `insufficient_funds` -> "insufficient funds". */
export function describeDecline(declineCode: string | undefined): string {
  if (declineCode && AUTHENTICATION_DECLINE_CODES.has(declineCode)) return 'authentication required';
  return declineCode ? declineCode.replace(/_/g, ' ') : 'the card issuer declined it';
}

// =============================================================================
// PAYMENT ERRORS (402 / 403 / 409 / 503 of the payment-bearing routes)
// =============================================================================

/** Machine-readable fields of a payment-route error body, carried on ApiClientError.details. */
export interface PaymentErrorDetails {
  declineCode?: string;
  quote?: Record<string, unknown>;
  ineligibleReason?: string;
}

/**
 * Build an ApiClientError from a payment-route error body. The route puts the specific
 * discriminator in `code` (`error` is only the generic HTTP label), so `code` wins; the
 * decline code, server quote and ineligible reason live at the top level of the body.
 */
export function apiErrorFromBody(status: number, body: unknown): ApiClientError {
  const parsed = BillingPaymentErrorResponseSchema.partial().safeParse(body);
  const data = parsed.success ? parsed.data : {};
  const details: PaymentErrorDetails = {
    declineCode: data.declineCode,
    quote: data.quote,
    ineligibleReason: data.ineligibleReason,
  };
  return new ApiClientError(
    data.message ?? 'An unexpected error occurred',
    data.code ?? data.error ?? 'UNKNOWN_ERROR',
    status,
    details,
  );
}

/** Payment details of an error thrown by a payment-bearing call (empty for other errors). */
export function getPaymentErrorDetails(err: unknown): PaymentErrorDetails {
  return err instanceof ApiClientError && err.details && typeof err.details === 'object'
    ? (err.details as PaymentErrorDetails)
    : {};
}

/** JSON fetch that preserves the payment error body (apiFetch drops `code`/`declineCode`/`quote`). */
async function paymentFetch<T>(endpoint: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const response = await fetch(endpoint, {
    method: init.method ?? 'GET',
    credentials: 'include',
    headers: init.body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const data: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    // Saving a card and turning on the add-on need a fresh session: hand off to the shared
    // re-authentication flow (which returns here) exactly as apiFetch does.
    if (response.status === 403 && (data as { code?: string }).code === 'REAUTHENTICATION_REQUIRED') {
      triggerReauthRequired();
      return new Promise<never>(() => {});
    }
    throw apiErrorFromBody(response.status, data);
  }
  return data as T;
}

/**
 * UsageInfo mirrors UsageResponseSchema in apps/api/src/routes/manager/billing.schemas.ts.
 * Fields: totalUnits, archivedUnits, billableUnits only — Fastify strips any extras.
 * Contract test: apps/landlord/src/__tests__/billing.contract.test.ts
 */
export interface UsageInfo {
  totalUnits: number;
  archivedUnits: number;
  billableUnits: number;
}

export interface CouponValidation {
  valid: boolean;
  bundleTemplate: BundleTemplate;
  expiresAt: string | null;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Get all available subscription bundles
 */
export async function getBundles(): Promise<{ bundles: BundleTemplate[] }> {
  return apiFetch<{ bundles: BundleTemplate[] }>('/landlord/api/billing/bundles');
}

/**
 * Phase 1: Subscribe to a bundle — creates Stripe Customer + SetupIntent.
 * Returns client secret for browser-side confirmSetup().
 */
export async function subscribe(params: {
  bundleTemplateId: string;
  couponCode?: string;
  billingAddress?: BillingAddress;
  /** Confirm the saved default card server-side instead of collecting a new one (BR-112). */
  useSavedCard?: boolean;
}): Promise<SubscribeResult> {
  return SubscribeResponseSchema.parse(
    await apiFetch<unknown>('/landlord/api/billing/subscribe', {
      method: 'POST',
      body: JSON.stringify(params),
    }),
  );
}

/**
 * Phase 2: Confirm subscription — server-side SetupIntent gate + emit CREATE_SUBSCRIPTION command.
 * Call this after stripe.confirmSetup() succeeds on the client.
 */
export async function confirmSubscription(params: {
  setupIntentId: string;
  subscriptionId: string;
}): Promise<ConfirmSubscriptionResult> {
  return apiFetch<ConfirmSubscriptionResult>('/landlord/api/billing/subscribe/confirm', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

/**
 * Get current subscription status
 */
export async function getSubscriptionStatus(): Promise<SubscriptionStatus> {
  return apiFetch<SubscriptionStatus>('/landlord/api/billing/subscription');
}

/**
 * Create a Stripe billing portal session
 */
export async function createPortalSession(): Promise<{ url: string }> {
  return apiFetch<{ url: string }>('/landlord/api/billing/portal-session', {
    method: 'POST',
  });
}

/**
 * Cancel subscription (at end of billing period)
 */
export async function cancelSubscription(params: {
  reason?: string;
}): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>('/landlord/api/billing/cancel', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

/**
 * Reactivate a cancelled subscription
 */
export async function reactivateSubscription(): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>('/landlord/api/billing/reactivate', {
    method: 'POST',
  });
}

/**
 * Validate a coupon code
 */
export async function validateCoupon(code: string): Promise<CouponValidation> {
  return apiFetch<CouponValidation>('/landlord/api/billing/redeem-coupon', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

/**
 * The shared wording of what every plan, the listing offering and agent support include (BR-113).
 * The only source of plan bullets in this app: they are served, never written here.
 */
export async function getPlanCopy(): Promise<PlanCopy> {
  return PlanCopyResponseSchema.parse(await apiFetch<unknown>('/api/public/plan-copy'));
}

/**
 * Get subscription features
 */
export async function getFeatures(): Promise<{ features: string[] }> {
  return apiFetch<{ features: string[] }>('/landlord/api/billing/features');
}

/**
 * Get one page of invoice history (newest first). Pass the previous page's `nextCursor` as `cursor`.
 */
export async function getInvoices(cursor?: string): Promise<InvoicesListResponse> {
  const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  return apiFetch<InvoicesListResponse>(`/landlord/api/billing/invoices${qs}`);
}

/**
 * Get usage information
 */
export async function getUsage(): Promise<UsageInfo> {
  return apiFetch<UsageInfo>('/landlord/api/billing/usage');
}

/**
 * Get invoice PDF URL
 */
export async function getInvoicePdfUrl(
  invoiceId: string,
): Promise<{ pdfUrl: string; hostedUrl: string; receiptUrl: string | null }> {
  return apiFetch<{ pdfUrl: string; hostedUrl: string; receiptUrl: string | null }>(`/landlord/api/billing/invoices/${invoiceId}/pdf`);
}

// =============================================================================
// DEFAULT PAYMENT METHOD (DC-47)
// =============================================================================

/** Create a SetupIntent for saving a card (the billing address drives tax). */
export async function createPaymentMethodSetupIntent(
  billingAddress: BillingAddress,
): Promise<PaymentMethodSetupIntentResult> {
  return paymentFetch<PaymentMethodSetupIntentResult>('/landlord/api/billing/payment-method/setup-intent', {
    method: 'POST',
    body: { billingAddress },
  });
}

/** Server-side gate after stripe.confirmSetup() — makes the card the default payment method. */
export async function confirmPaymentMethod(setupIntentId: string): Promise<PaymentMethodConfirmResult> {
  return paymentFetch<PaymentMethodConfirmResult>('/landlord/api/billing/payment-method/confirm', {
    method: 'POST',
    body: { setupIntentId },
  });
}

// =============================================================================
// AGENT SUPPORT ADD-ON (DC-49)
// =============================================================================

export async function getAgentAddonQuote(idempotencyKey?: string): Promise<AgentAddonQuote> {
  // With the caller's persisted key the server returns the PINNED quote of an unresolved attempt (BR-102).
  const query = idempotencyKey ? `?idempotencyKey=${encodeURIComponent(idempotencyKey)}` : '';
  return paymentFetch<AgentAddonQuote>(`/landlord/api/billing/agent-addon/quote${query}`);
}

export async function enableAgentAddon(params: AgentAddonEnableParams): Promise<AgentAddonEnableResult> {
  return paymentFetch<AgentAddonEnableResult>('/landlord/api/billing/agent-addon/enable', {
    method: 'POST',
    body: params,
  });
}

export async function disableAgentAddon(): Promise<AgentAddonStatusResult> {
  return paymentFetch<AgentAddonStatusResult>('/landlord/api/billing/agent-addon/disable', { method: 'POST' });
}

export async function undoDisableAgentAddon(): Promise<AgentAddonStatusResult> {
  return paymentFetch<AgentAddonStatusResult>('/landlord/api/billing/agent-addon/undo-disable', { method: 'POST' });
}
